// Generische Offline-Outbox: eine persistierte FIFO-Queue für schreibende Mutationen, die offline
// oder bei transientem Serverfehler nicht direkt zugestellt werden konnten. Beim Reconnect (online-Event)
// und beim Boot wird sie idempotent (client_uuid) wieder abgespielt.
// → North-Star #1 (ausfallsicher) + Audit-Cluster B-1/B-2/B-3/#6.
//
// SCOPE dieser Welle: trägt ausschließlich IDEMPOTENTE, append-only POSTs (Zeitbuchung). Der Server
// dedupliziert per UNIQUE(order_id, client_uuid) → ein wiederholter Replay erzeugt KEINE Doppelbuchung.
// Versionierte Updates (OCC/409) gehören NICHT hier rein, solange es dafür keine Konflikt-Reconcile-Policy
// gibt — sonst würde ein blinder Replay einen fremden Stand überschreiben.

import { notifyUnauthorized } from '../api/auth-events'

// Pro-Konto-Scope (R2/1.5): auf einem GETEILTEN Werkstatt-Tablet darf die Queue von Konto A niemals unter der
// Session von Konto B abgespielt werden — sonst wird A's Stempel B zugeschrieben (Fehlzuordnung = Mandanten-/
// GoBD-Bruch). Der Schlüssel trägt daher die user_id; der Flush arbeitet immer nur auf dem AKTUELLEN Konto-Key.
// Ohne gesetztes Konto (vor dem Login) fällt er auf den konto-losen Basis-Key zurück (Rückwärtskompatibilität +
// einmalige Migration beim ersten Login). Dead-Letter: endgültig abgelehnte Items (terminaler 4xx) landen im
// failed-Bucket statt still gelöscht zu werden — Invariante #1 (NIEMALS still verloren) gilt auch hier.
const BASE_KEY = 'wf.outbox.v1'
const BASE_FAILED_KEY = 'wf.outbox.failed.v1'
let account: string | null = null

function storageKey(): string {
  return account ? `${BASE_KEY}.${account}` : BASE_KEY
}
function failedKey(): string {
  return account ? `${BASE_FAILED_KEY}.${account}` : BASE_FAILED_KEY
}

/** Alt-Items aus `fromKey` in `toKey` übernehmen (dedup per id), dann `fromKey` räumen. Einmalige Migration. */
function mergeStorage(fromKey: string, toKey: string): void {
  try {
    const rawFrom = localStorage.getItem(fromKey)
    if (!rawFrom) return
    const fromItems: unknown = JSON.parse(rawFrom)
    if (!Array.isArray(fromItems) || fromItems.length === 0) {
      localStorage.removeItem(fromKey)
      return
    }
    let toItems: OutboxItem[] = []
    try {
      const rawTo = localStorage.getItem(toKey)
      const parsed: unknown = rawTo ? JSON.parse(rawTo) : []
      if (Array.isArray(parsed)) toItems = parsed as OutboxItem[]
    } catch {
      toItems = []
    }
    const seen = new Set(toItems.map((i) => i.id))
    for (const it of fromItems as OutboxItem[]) if (it && !seen.has(it.id)) toItems.push(it)
    localStorage.setItem(toKey, JSON.stringify(toItems))
    localStorage.removeItem(fromKey)
  } catch {
    /* Persistenz nicht verfügbar — best effort, kein Absturz. */
  }
}

/**
 * Bindet die Outbox an ein Konto (user_id): Queue + Dead-Letter laufen ab jetzt unter dem konto-eigenen Schlüssel.
 * Übernimmt EINMALIG die konto-losen Alt-Items (aus der Zeit vor der pro-Konto-Umstellung bzw. vor dem Login).
 * Der Aufrufer (App-Effekt auf user.id) ruft danach flushOutbox — so wird nur die Queue DES eingeloggten Kontos
 * unter dessen Session zugestellt.
 */
export function setOutboxAccount(userId: number): void {
  const next = String(userId)
  if (account === next) return
  account = next
  mergeStorage(BASE_KEY, storageKey())
  mergeStorage(BASE_FAILED_KEY, failedKey())
}

/** Hebt die Konto-Bindung auf (Logout). Die konto-eigene Queue bleibt unter ihrem Schlüssel erhalten (dormant). */
export function clearOutboxAccount(): void {
  account = null
}

export interface OutboxItem {
  /** Lokale, stabile ID = client_uuid (zugleich der serverseitige Idempotenz-Schlüssel). */
  id: string
  method: 'POST' | 'PATCH' | 'DELETE'
  /** Pfad OHNE führendes /api/ (wie api/client.request) — z. B. 'orders/12/time'. */
  path: string
  body: Record<string, unknown>
  createdAt: number
}

export interface FlushResult {
  /** Direkt gebuchte (201) zugestellte und entfernte Items. */
  sent: number
  /** In einen Auto-Korrekturantrag umgewandelte (202) Nachzügler — zugestellt/entfernt, aber KEIN Stempel (R2/1.4). */
  converted: number
  /** Endgültig abgelehnte Items (nicht-wiederholbarer 4xx) — entfernt, damit die Queue nicht verstopft. */
  rejected: OutboxItem[]
  /** Verbleibende Items (offline/5xx/401 → Retry beim nächsten online/Boot). */
  remaining: number
}

export function loadOutbox(): OutboxItem[] {
  try {
    const raw = localStorage.getItem(storageKey())
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as OutboxItem[]) : []
  } catch {
    return []
  }
}

function persist(items: OutboxItem[]): void {
  try {
    localStorage.setItem(storageKey(), JSON.stringify(items))
  } catch {
    // localStorage voll/blockiert (Private-Mode) — wir verlieren die Persistenz, nicht den laufenden Prozess.
  }
}

export function outboxSize(): number {
  return loadOutbox().length
}

/** Reiht ein Item ein. Idempotent in der Queue selbst: gleiche id wird nicht doppelt eingereiht. */
export function enqueue(item: Omit<OutboxItem, 'createdAt'> & { createdAt?: number }): void {
  const items = loadOutbox()
  if (items.some((i) => i.id === item.id)) return
  items.push({ ...item, createdAt: item.createdAt ?? Date.now() })
  persist(items)
}

export function removeFromOutbox(id: string): void {
  persist(loadOutbox().filter((i) => i.id !== id))
}

function loadFailed(): OutboxItem[] {
  try {
    const raw = localStorage.getItem(failedKey())
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as OutboxItem[]) : []
  } catch {
    return []
  }
}

/** Endgültig abgelehntes Item aus der Live-Queue in den durablen Dead-Letter-Bucket verschieben (statt löschen). */
export function deadLetter(id: string): void {
  const items = loadOutbox()
  const item = items.find((i) => i.id === id)
  persist(items.filter((i) => i.id !== id))
  if (!item) return
  const failed = loadFailed()
  if (!failed.some((i) => i.id === id)) {
    failed.push(item)
    try {
      localStorage.setItem(failedKey(), JSON.stringify(failed))
    } catch {
      /* Persistenz nicht verfügbar — wenigstens nicht stillschweigend: siehe console.error unten. */
    }
  }
  // Sichtbare Spur statt stillem Verlust gebuchter Zeit.
  console.error('[outbox] Schreibvorgang endgültig abgelehnt, in Dead-Letter verschoben:', item.path, item.body)
}

/** Endgültig fehlgeschlagene Items (für die Dead-Letter-/Recovery-Ansicht). */
export function loadFailedOutbox(): OutboxItem[] {
  return loadFailed()
}

function persistFailed(items: OutboxItem[]): void {
  try {
    localStorage.setItem(failedKey(), JSON.stringify(items))
  } catch {
    /* Persistenz nicht verfügbar — kein Absturz. */
  }
}

/** Anzahl endgültig fehlgeschlagener Items (für den Status-Indikator). */
export function failedOutboxSize(): number {
  return loadFailed().length
}

/** Verwirft ein Dead-Letter-Item, nachdem der Nutzer es zur Kenntnis genommen hat (z. B. neu gestempelt). */
export function discardFailedOutbox(id: string): void {
  persistFailed(loadFailed().filter((i) => i.id !== id))
}

let flushing = false

/**
 * Spielt die Queue in FIFO-Reihenfolge ab. Sequentiell, damit ein Offline-/5xx-Stopp den Rest schont
 * (kein paralleler Sturm beim Reconnect). Re-entrancy-Guard: ein zweiter Aufruf während eines laufenden
 * Flushes ist ein No-op.
 */
export async function flushOutbox(): Promise<FlushResult> {
  if (flushing) return { sent: 0, converted: 0, rejected: [], remaining: outboxSize() }
  flushing = true
  const result: FlushResult = { sent: 0, converted: 0, rejected: [], remaining: 0 }
  try {
    for (const item of loadOutbox()) {
      let res: Response
      try {
        res = await fetch(`/api/${item.path}`, {
          method: item.method,
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(item.body),
        })
      } catch {
        // Netzfehler → wir sind (wieder) offline. Rest bleibt erhalten, nächster online/Boot versucht es erneut.
        break
      }
      if (res.ok) {
        removeFromOutbox(item.id)
        // 202 = der Nachzügler wurde in einen Korrekturantrag umgewandelt (nicht direkt gebucht); getrennt zählen,
        // damit die UI „X als Antrag eingereicht" statt „X gebucht" melden kann. Item ist zugestellt → entfernt.
        if (res.status === 202) result.converted++
        else result.sent++
        continue
      }
      if (res.status === 401) {
        // Session tot: NICHT verwerfen (nach Re-Login erneut zustellbar), zentrales 401-Handling auslösen, stoppen.
        notifyUnauthorized()
        break
      }
      if (res.status >= 500) {
        // transienter Serverfehler → behalten, später erneut versuchen.
        break
      }
      // 400/403/404/409/422: nicht-wiederholbar (würde die Queue sonst dauerhaft verstopfen).
      // NICHT still löschen — das wäre Verlust gebuchter Arbeitszeit. In den Dead-Letter-Bucket
      // verschieben (durabel + auffindbar) und dem Aufrufer melden.
      deadLetter(item.id)
      result.rejected.push(item)
    }
  } finally {
    flushing = false
  }
  result.remaining = outboxSize()
  return result
}

let installed = false

/**
 * Einmalig: Outbox abspielen bei Reconnect (online), Tab-Fokus (focus/visibilitychange) und beim Boot.
 * Die zusätzlichen Trigger schließen die Konvergenz-Lücke, wenn ein transienter 5xx im Online-Zustand
 * passiert (dann feuert KEIN online-Event) — die Queue heilt sich beim nächsten Interagieren statt erst
 * beim nächsten App-Boot. `onFlushed` erlaubt dem Aufrufer (main.tsx), nach zugestellten Items die
 * betroffenen Queries zu invalidieren. Nur in der Produktion (main.tsx) verdrahtet.
 */
export function installOutboxAutoFlush(onFlushed?: (result: FlushResult) => void): void {
  if (installed) return
  installed = true
  const run = (): void => {
    void flushOutbox().then((result) => {
      if (onFlushed) onFlushed(result)
    })
  }
  window.addEventListener('online', run)
  window.addEventListener('focus', run)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') run()
  })
  if (navigator.onLine) run()
}
